import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Mail, Lock, ArrowRight, ChevronLeft, Headphones, Mic, BookOpen, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { T, PrimaryBtn, NeonInput, HudFrame, AIPropsBackground, AuthBrand, useClickSound } from "@/pages/AuthPreviewShared";

/**
 * Login — EnglishTest.UK daylight theme + real auth wiring.
 */
export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const play = useClickSound();

  useEffect(() => {
    const id = "lingoq-preview-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Rajdhani:wght@500;600;700&family=IBM+Plex+Mono:wght@400;500;600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      nav(u.onboarded ? "/app" : "/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const useDemo = () => { setEmail("demo@lingoq.ai"); setPassword("demo1234"); };

  return (
    <div className="min-h-screen relative grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]" style={{ background: T.bg, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="login-root">
      <style>{`
        .display { font-family: 'Rajdhani', sans-serif; letter-spacing: -0.01em; }
        .mono    { font-family: 'IBM Plex Mono', monospace; }
      `}</style>

      {/* LEFT */}
      <div className="relative overflow-hidden p-8 lg:p-14 hidden lg:flex flex-col justify-between"
        style={{ background: `linear-gradient(135deg, ${T.bg2} 0%, ${T.cream}88 100%)`, borderRight: `1px solid ${T.border}` }}>
        <AIPropsBackground />
        <div className="relative">
          <AuthBrand />
        </div>
        <div className="relative">
          <div className="mono text-[10px] tracking-[0.3em] mb-3" style={{ color: T.primary }}>// SYSTEM LOGIN</div>
          <h1 className="display text-5xl lg:text-6xl font-bold leading-[1.0]" style={{ color: T.text }}>
            Welcome back.<br />
            <span style={{ color: T.primary, textShadow: `0 0 22px ${T.primaryGlow}` }}>Examiners</span> are<br />ready when you are.
          </h1>
          <p className="text-base mt-5 max-w-md leading-relaxed" style={{ color: T.textMuted }}>
            Re-engage your training session. Pick up where you left off — every band, every skill, every drill is saved.
          </p>
          <div className="mt-9 grid grid-cols-2 gap-3 max-w-md">
            {[
              { Icon: Headphones, l: "Listening" },
              { Icon: Pencil,     l: "Writing" },
              { Icon: BookOpen,   l: "Reading" },
              { Icon: Mic,        l: "Speaking" },
            ].map((k, i) => (
              <HudFrame key={i} className="p-3" color={T.primary}>
                <div className="flex items-center gap-2 mono text-[9px] tracking-[0.25em]" style={{ color: T.textMuted }}>
                  <k.Icon className="w-3.5 h-3.5" style={{ color: T.primary }} /> {k.l.toUpperCase()}
                </div>
                <div className="display font-bold text-xl mt-1" style={{ color: T.text }}>Tracked</div>
              </HudFrame>
            ))}
          </div>
        </div>
        <div className="relative mono text-[10px] tracking-[0.3em]" style={{ color: T.textMuted }}>
          <span style={{ color: T.success }}>{">"}</span> 27,400 candidates training right now
        </div>
      </div>

      {/* RIGHT */}
      <div className="relative flex items-center justify-center p-6 sm:p-10 lg:p-16">
        <div className="absolute inset-0 lg:hidden">
          <AIPropsBackground />
        </div>
        <div className="relative w-full max-w-md">
          <div className="lg:hidden mb-8">
            <AuthBrand />
          </div>
          <Link to="/" onClick={() => play("hover")}
            className="inline-flex items-center gap-1 mono text-[10px] tracking-[0.3em] hover:underline mb-6"
            style={{ color: T.textMuted }} data-testid="back-to-landing">
            <ChevronLeft className="w-3 h-3" /> BACK TO HOME
          </Link>
          <HudFrame color={T.primary} className="p-1">
            <div className="rounded-sm p-7 lg:p-9" style={{ background: T.cardBg, border: `1px solid ${T.borderSoft}` }}>
              <div className="mono text-[10px] tracking-[0.3em] mb-2" style={{ color: T.success }}>// AUTHENTICATE</div>
              <h2 className="display text-4xl font-bold mb-2" style={{ color: T.text }}>Sign in</h2>
              <p className="mono text-xs mb-7" style={{ color: T.textMuted }}>Re-engage your training session.</p>
              <form onSubmit={submit} className="space-y-5">
                <NeonInput label="Email Address" icon={Mail} type="email" placeholder="you@email.com"
                  value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
                <NeonInput label="Password" icon={Lock} type="password" placeholder="••••••••••"
                  value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
                <PrimaryBtn type="submit" disabled={loading} className="w-full h-16 text-base" data-testid="login-submit-btn">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Signing in…</> : <>AUTHENTICATE <ArrowRight className="w-5 h-5" /></>}
                </PrimaryBtn>
              </form>
              <button onClick={useDemo} data-testid="use-demo-btn"
                className="mt-4 w-full mono text-[10px] tracking-[0.3em] uppercase transition-colors"
                style={{ color: T.textMuted }}
                onMouseEnter={(e) => e.currentTarget.style.color = T.primary}
                onMouseLeave={(e) => e.currentTarget.style.color = T.textMuted}>
                Use demo account →
              </button>
              <div className="text-sm text-center mt-6" style={{ color: T.textMuted }}>
                New to EnglishTest.UK?{" "}
                <Link to="/signup" className="font-semibold" style={{ color: T.primary }}>Create a free profile →</Link>
              </div>
            </div>
          </HudFrame>
          <div className="mt-6 flex items-center justify-between mono text-[9px] tracking-[0.3em]" style={{ color: T.textMuted }}>
            <span>// 256-BIT TLS · GDPR · SOC-2 READY</span>
            <span>v2.6 · UK</span>
          </div>
        </div>
      </div>
    </div>
  );
}
